/**
 * Recover from "Failed to fetch dynamically imported module" after a deploy.
 *
 * Every deploy gives the code chunks new hashed file names and the old ones disappear. A tab that
 * was opened before the deploy still references the old names, so the first lazily-loaded chunk it
 * needs (e.g. the Excel reader on a Direct Shear upload) 404s. Vite announces that with a
 * `vite:preloadError` event; we answer by reloading the page once so it picks up the new build.
 *
 * Loop guard: if the same failure happens again within RELOAD_GUARD_MS of a recovery reload, we
 * let the original error surface instead of reloading forever (e.g. a genuinely missing file).
 */
const RELOAD_STAMP_KEY = 'alis.staleBuildReloadAt';
const RELOAD_GUARD_MS = 60_000;

const recentlyReloaded = (): boolean => {
  try {
    return Date.now() - Number(sessionStorage.getItem(RELOAD_STAMP_KEY) || '0') < RELOAD_GUARD_MS;
  } catch {
    return false;
  }
};

export const initStaleBuildRecovery = (): void => {
  window.addEventListener('vite:preloadError', (event) => {
    if (recentlyReloaded()) return; // already tried once — show the real error
    event.preventDefault(); // suppress the error; the reload below replaces this page
    try {
      sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
    } catch {
      /* private mode — reload anyway */
    }
    window.location.reload();
  });
};
